import React, { createContext, useContext, useEffect, useReducer, useCallback } from "react";

interface LayoutState {
  backgroundColor: string;
  // Add specific state properties here if needed
}

interface LayoutAction {
  type: string;
  payload?: unknown;
}

interface LayoutContextType {
  state: LayoutState;
  dispatch: React.Dispatch<LayoutAction>;
}

const LayoutContext = createContext<LayoutContextType | null>(null);

export const LayoutProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const initialState: LayoutState = {
    backgroundColor: "white", // Default background color
  };

  const reducer = (state: LayoutState, action: LayoutAction): LayoutState => {
    switch (action.type) {
      case "SET_BACKGROUND_COLOR":
        return {
          ...state,
          backgroundColor: action.payload as string,
        };
      // Define your reducer logic here
      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  return (
    <LayoutContext.Provider value={{ state, dispatch }}>
      {children}
    </LayoutContext.Provider>
  );
};

export enum Colors {
  WHITE = "white",
  BLACK = "black",
  LIGHT_GRAY = "#F9FAFB",
  // Add more colors as needed
}

export const useLayoutCommon = () => {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error("useLayout must be used within a LayoutProvider");
  }
  const { state, dispatch } = context;
  const { backgroundColor } = state;

  useEffect(() => {
    document.body.style.backgroundColor = backgroundColor;
    document.documentElement.style.backgroundColor = backgroundColor;
  }, [backgroundColor]);

  const setBackgroundColor = useCallback((color: Colors | string) => {
    dispatch({ type: "SET_BACKGROUND_COLOR", payload: color });
  }, [dispatch]);

  return {
    ...state,
    setBackgroundColor,
  };
};
